import { useState } from 'react'
import PropTypes from '~/lib/propTypes'
import { Button, space, InputV2 as Input, Alert } from '@ticketswap/solar'
import styled from '@emotion/styled'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { AUTHENTICATION_STARTED, AUTHENTICATION_COMPLETED } from '~/lib/tracking-events'
import { track } from '~/utils/analytics'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import { SEND_LOGIN_LINK } from '~/graphql/mutations/sendLoginLink'
import { useMutation } from '@apollo/client'
import { SEND_LOGIN_CODE } from '~/graphql/mutations/sendLoginCode'

const Form = styled.form`
  display: grid;
  grid-gap: ${space[8]};
`

const EmailAuthForm = ({
  t,
  redirectUrl,
  onEmailSignup,
  onEmailLogin,
  onLoginWithCode,
  onSignupWithCode,
  method = 'email',
}) => {
  const [errorMessage, setErrorMessage] = useState(null)
  const { setEmail } = useLoggedInUser()
  const [sendLink, { loading: loadingSendingLink }] = useMutation(SEND_LOGIN_LINK)
  const [sendCode, { loading: loadingSendingCode }] = useMutation(SEND_LOGIN_CODE)

  const handleErrors = async errors => {
    await track(AUTHENTICATION_COMPLETED, {
      method,
      isNew: false,
      error: errors[0].code,
    })

    return setErrorMessage(errors[0].message)
  }

  const handleSendLoginLink = async email => {
    const {
      data: { sendLoginLink },
    } = await sendLink({
      variables: {
        input: {
          email,
          redirectTo: redirectUrl || window.location.href,
        },
      },
    })

    const isNewUser = sendLoginLink.errors.find(error => error.code === 'EMAIL_DOES_NOT_EXIST')

    if (isNewUser) {
      await setEmail(email)

      return onEmailSignup()
    }

    if (sendLoginLink.errors.length) {
      return handleErrors(sendLoginLink.errors)
    }

    await track(AUTHENTICATION_COMPLETED, {
      method,
      isNew: false,
    })

    await setEmail(email)
    return onEmailLogin()
  }

  const handleSendLoginCode = async email => {
    const { data } = await sendCode({
      onError: () => setErrorMessage(t('common:generic.somethingWentWrong')),
      variables: {
        input: {
          email,
        },
      },
    })

    if (!data) return

    const {
      sendLoginCode: { warnings, errors },
    } = data

    if (errors.length) {
      return handleErrors(errors)
    }

    const isNewUser = warnings.find(warning => warning.code === 'NO_MATCHING_USER_FOUND_FOR_EMAIL')

    if (isNewUser) {
      await setEmail(email)

      return onSignupWithCode()
    }

    await setEmail(email)
    return onLoginWithCode()
  }

  const formik = useFormik({
    validationSchema: yup.object().shape({
      email: yup.string().email().required(t('common:generic.required')),
    }),
    initialValues: {
      email: '',
    },
    validateOnChange: false,
    onSubmit: async ({ email }) => {
      setErrorMessage(null)

      await track(AUTHENTICATION_STARTED, {
        method,
      })

      method === 'email' ? handleSendLoginLink(email) : handleSendLoginCode(email)
    },
  })

  const { handleChange, values, isValid, dirty, handleSubmit } = formik

  return (
    <Form onSubmit={handleSubmit}>
      {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
      <Input
        type="email"
        name="email"
        id="email"
        aria-label={t('common:auth.signupEmail.fields.email.label')}
        value={values.email}
        onChange={handleChange}
        placeholder={t('common:auth.login.inputs.email')}
      />
      <Button
        variant="success"
        type="submit"
        fullWidth
        loading={loadingSendingLink || loadingSendingCode}
        disabled={!isValid || !dirty}
      >
        {t('common:auth.login.buttons.email')}
      </Button>
    </Form>
  )
}

EmailAuthForm.propTypes = {
  t: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string,
  onEmailLogin: PropTypes.func.isRequired,
  onEmailSignup: PropTypes.func.isRequired,
  onLoginWithCode: PropTypes.func.isRequired,
  onSignupWithCode: PropTypes.func.isRequired,
  method: PropTypes.oneOf(['email', 'code']),
}

export default EmailAuthForm
